import React, { useState, useEffect, Fragment } from "react";
import InlineErrors from "components/shared/InlineErrors";
import InlineDescription from "./InlineDescription";
import "@wdpr/single-select";

const InputSelect = React.forwardRef((props, ref) => {
	const {
		id,
		label,
		name,
		required,
		requiredText = "",
		description,
		value = "",
		placeholder = "",
		handleChange,
		validateInput,
		options,
		modifier,
		notEmptyMessage,
		inlineErrors,
		showInlineErrors = true,
		isMobileView,
		useHyperionTheme,
		focus,
	} = props;
	const mainClass = "help-input-select";
	const modifierClass = modifier ? `${mainClass}--${modifier}` : "";
	const [hasError, setHasError] = useState();
	const [isExpanded, setIsExpanded] = useState(false);
	const [singleSelectValue, setSingleSelectValue] = useState({});
	const additionalSelectProps = {};

	const singleSelectInitialConfig = () => {
		document.querySelectorAll("wdpr-single-select")?.forEach((singleSelect) => {
			singleSelect.shadowRoot
				?.querySelector("#custom-dropdown-button")
				?.removeAttribute("aria-required");
		});
	};

	useEffect(() => {
		if (focus) {
			document.getElementById(id)?.focus();
			document.getElementById("root").scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "end",
			});
		}
		if (!useHyperionTheme) return;
		singleSelectInitialConfig();
		const itemSelectedHandler = (event) => {
			setSingleSelectValue({ target: { name, value: event.detail?.key } });
		};
		document
			.getElementById(id)
			?.addEventListener("item-selected", itemSelectedHandler);
		return () =>
			window.removeEventListener("item-selected", itemSelectedHandler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		!!inlineErrors?.[name] && setHasError(true);
	}, [inlineErrors, name]);

	useEffect(() => {
		singleSelectValue.target && handleChange(singleSelectValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [singleSelectValue]);

	if (!name || !options || !Array.isArray(options) || !options?.length) {
		return null;
	}

	if (inlineErrors?.[id]) {
		additionalSelectProps.invalid = true;
	} else {
		delete additionalSelectProps.invalid;
	}

	return (
		<div className={`${mainClass} ${modifierClass}`} ref={ref}>
			{useHyperionTheme ? (
				<wdpr-single-select
					id={id}
					options={JSON.stringify(
						options
							?.filter((option) => !!option.value)
							.map((option) => ({
								key: option.value,
								value: option.text,
							}))
					)}
					label={label ? `${label}${required ? "*" : ""}` : placeholder}
					custom-aria-label={`${label}${
						required && requiredText ? ` ${requiredText}` : ""
					}`}
					onBlur={() => {
						required &&
							notEmptyMessage &&
							validateInput &&
							validateInput(
								name,
								value,
								required,
								notEmptyMessage,
								null,
								null,
								setHasError
							);
					}}
					value={value}
					required-field={required}
					{...additionalSelectProps}
				></wdpr-single-select>
			) : (
				<Fragment>
					{label && (
						<label
							className={`${mainClass}__label${
								hasError ? ` ${mainClass}__label--error` : ""
							}${value ? ` ${mainClass}__label--filled` : ""}`}
							htmlFor={!hasError ? id : undefined}
						>
							{`${label}${required ? "*" : ""}`}
						</label>
					)}
					{description?.modalContent && (
						<InlineDescription
							{...description}
							id={id}
							isMobileView={isMobileView}
							position="top"
						/>
					)}
					<select
						className={`${mainClass}__select${
							hasError ? ` ${mainClass}__select--error` : ""
						}${value ? ` ${mainClass}__select--filled` : ""}`}
						id={id}
						name={name}
						value={value}
						required={required}
						aria-required={required}
						aria-describedby={
							[
								hasError ? `${id}_error` : null,
								(description?.modalContent || description?.text) ? `${id}-message-instructions` : null,
							]
								.filter(Boolean)
								.join(' ') || undefined
						}
						aria-expanded={isExpanded}
						onClick={() => setIsExpanded((prevState) => !prevState)}
						onChange={(e) => {
							handleChange && handleChange(e);
						}}
						onBlur={() => {
							required &&
								notEmptyMessage &&
								validateInput &&
								validateInput(
									name,
									value,
									required,
									notEmptyMessage,
									null,
									null,
									setHasError
								);
							setIsExpanded(false);
						}}
					>
						{options?.map((option, key) => (
							<option
								key={key}
								value={option.value}
								className={`${mainClass}__option`}
							>
								{option.text}
							</option>
						))}
					</select>
				</Fragment>
			)}
			{inlineErrors && showInlineErrors && (
				<InlineErrors validate={id} errorMessages={inlineErrors} />
			)}
			{description?.text && (
				<InlineDescription
					{...description}
					id={id}
					isMobileView={isMobileView}
					position="bottom"
				/>
			)}
		</div>
	);
});

InputSelect.displayName = "InputSelect";
export default InputSelect;
